/**
 * ProcessorCostTemplatesView — Super Admin only.
 * Confidential MSC actual processor costs.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { displayProcessor } from '../../lib/processorDisplay';

const fmtAmount = (l) => {
  if (l.unit === 'info') return l.billing_basis || '—';
  if (l.unit === 'bps') return `${l.amount} bps`;
  if (l.unit === 'percent') return `${l.amount}%`;
  return `$${Number(l.amount).toFixed(4)}`;
};

const PAYOUT_LABEL = { ACTIVE: 'live for payouts', PENDING_APPROVAL: 'waiting for a second Super Admin', NO_PROCESSOR_SCHEDULE: 'no processor schedule', NO_PAYOUT_LINES: 'no payout lines', ERROR: 'error' };

function PayoutLine({ t, onDone }) {
  const results = t.payout_sync?.results || [];
  if (!results.length) return null;
  const pending = results.some((r) => r.status === 'PENDING_APPROVAL');
  const approve = async () => {
    try {
      const r = await api.request(`/schedule-a/processor-cost-templates/${t.id}/approve-payouts`, { method: 'POST' });
      const err = r.results.find((x) => x.error && x.status !== 'ACTIVE');
      if (err) toast.error(err.error); else toast.success('Approved — payouts now use these processor costs');
      onDone();
    } catch (e) { toast.error(e.message || 'Approval failed'); }
  };
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs" data-testid={`payout-status-${t.id}`}>
      <span className="text-muted-foreground">Payouts: {results.map((r) => `${displayProcessor(r.provider_slug)} ${PAYOUT_LABEL[r.status] || r.status}${r.error ? ` (${r.error})` : ''}`).join(' · ')}</span>
      {pending && t.status === 'active' && <Button size="sm" variant="outline" className="h-7" onClick={approve} data-testid={`approve-payouts-${t.id}`}>Approve for payouts</Button>}
    </div>
  );
}

const UNITS = ['bps', 'per_transaction', 'per_event', 'flat_monthly', 'flat_annual', 'one_time', 'percent', 'info'];

function groupByCategory(lines) {
  const groups = new Map();
  for (const l of lines) {
    const cat = l.category || '';
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat).push(l);
  }
  return Array.from(groups.entries());
}

export default function ProcessorCostTemplatesView() {
  return <CostTemplatesTable variant="processor" />;
}

export function CostTemplatesTable({ variant }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);

  const load = async () => {
    try {
      const d = variant === 'processor'
        ? await api.getProcessorCostTemplates()
        : await api.getIsoCostTemplates();
      setTemplates(d || []);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const title = variant === 'processor' ? 'Processor Cost Templates (MSC True Cost)' : 'Legacy v1 ISO Cost Templates';
  const subtitle = variant === 'processor'
    ? 'Confidential — MSC actual processor costs. Never exposed to ISOs.'
    : 'Cost schedules assigned to ISOs. Any line-item not marked up passes through at MSC base cost.';

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <Card data-testid={`${variant}-cost-view`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>{title}</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        </div>
        <Button size="sm" onClick={() => setShowDialog(true)} data-testid={`${variant}-new-template-btn`}>
          <Plus className="h-3 w-3 mr-1" /> New Template
        </Button>
      </CardHeader>
      <CardContent>
        {templates.length === 0 && (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No {variant} cost templates yet.
          </p>
        )}
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.id} className="p-3 border rounded-lg" data-testid={`${variant}-template-${t.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{t.name} <Badge variant="outline" className="ml-1">v{t.version}</Badge></div>
                  <div className="text-xs text-muted-foreground">
                    {displayProcessor(t.processor_slug)} • Effective {t.effective_date}
                    {t.is_high_risk && ' • High Risk'}
                  </div>
                  {variant === 'processor' && <PayoutLine t={t} onDone={load} />}
                </div>
                <Badge variant={t.status === 'active' ? 'default' : 'secondary'}>{t.status}</Badge>
              </div>
              <div className="mt-3 overflow-x-auto"><table className="w-full min-w-[360px] text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th>Fee</th><th>Unit</th><th>Billing Basis</th><th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {groupByCategory(t.cost_lines || []).map(([cat, lines]) => (
                    <React.Fragment key={cat}>
                      {cat && (
                        <tr className="bg-muted/40">
                          <td colSpan={4} className="py-1 px-2 font-semibold uppercase text-[10px] tracking-wide">
                            {cat}
                          </td>
                        </tr>
                      )}
                      {lines.map((l, i) => (
                        <tr key={`${cat}-${i}`} className="border-b border-dashed last:border-0">
                          <td className="py-1" title={l.notes || undefined}>{l.label || l.fee_code}</td>
                          <td className="text-muted-foreground">{l.unit}</td>
                          <td className="text-muted-foreground text-[10px]">{l.billing_basis || ''}</td>
                          <td className="text-right font-mono">
                            {fmtAmount(l)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table></div>
            </div>
          ))}
        </div>
      </CardContent>

      {showDialog && (
        <CostTemplateDialog
          variant={variant}
          onClose={() => setShowDialog(false)}
          onSaved={() => { setShowDialog(false); load(); }}
        />
      )}
    </Card>
  );
}

const PLATFORM_OPTIONS = [['maverick', 'TSYS'], ['fiserv', 'Fiserv'], ['north_epx', 'Global/EPX'], ['cygma', 'Cygma'], ['square', 'Square']];

function CostTemplateDialog({ variant, onClose, onSaved }) {
  const [name, setName] = useState('TSYS MSC Standard');
  const [processorSlug, setProcessorSlug] = useState('maverick');
  const [description, setDescription] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [costLines, setCostLines] = useState([]);
  const [source, setSource] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const platform = PLATFORM_OPTIONS.find(([v]) => v === processorSlug)[1];
    setName((n) => (!n || / MSC Standard$/.test(n) ? `${platform} MSC Standard` : n));
    api.request(`/admin/schedule-a/platform-lines?platform=${encodeURIComponent(platform)}`)
      .then((d) => { setCostLines(d.lines.map(({ ref, ...l }) => l)); setSource(`${d.version_label} (${d.status.toLowerCase()})`); })
      .catch((e) => toast.error(e.message || 'Could not load the Schedule A lines'));
  }, [processorSlug]);

  const setLine = (i, k, v) => setCostLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const [reading, setReading] = useState(false);
  const readSheet = async (file) => {
    if (!file) return;
    setReading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const d = await api.request('/schedule-a/processor-cost-templates/extract', { method: 'POST', body: fd });
      const key = (l) => (l.label || '').toLowerCase().trim();
      setCostLines((ls) => {
        const byKey = new Map(d.lines.map((l) => [key(l), l]));
        const merged = ls.map((l) => (byKey.has(key(l)) ? { ...l, unit: byKey.get(key(l)).unit, amount: byKey.get(key(l)).amount, ai_extracted: true } : l));
        const have = new Set(ls.map(key));
        return [...merged, ...d.lines.filter((l) => !have.has(key(l)))];
      });
      if (d.effective_date) setEffectiveDate(d.effective_date);
      if (!description) setDescription(`From ${d.file_name}`);
      toast.success(`AI read ${d.lines.length} cost lines — check every amount before saving`);
    } catch (e) { toast.error(e.message || 'Could not read that file'); } finally { setReading(false); }
  };
  const save = async () => {
    if (!name || costLines.length === 0) {
      toast.error('Name and at least one cost line required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name, processor_slug: processorSlug, description, effective_date: effectiveDate,
        cost_lines: costLines.map(({ feeds_payouts, ...l }) => ({ ...l, amount: parseFloat(l.amount) || 0 })),
      };
      if (variant === 'processor') await api.createProcessorCostTemplate(body);
      else await api.createIsoCostTemplate(body);
      toast.success(variant === 'processor' ? 'Template saved — a second Super Admin approves it for payouts' : 'Template created');
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid={`${variant}-cost-dialog`}>
        <DialogHeader>
          <DialogTitle>New {variant === 'processor' ? 'Processor' : 'ISO'} Cost Template</DialogTitle>
          <DialogDescription>
            Every line of the MSC Schedule A for this platform, top to bottom, prefilled from {source || 'the Schedule A'}. Change any amount and save — every save creates a new version.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {variant === 'processor' && (
            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-dashed p-3 hover:bg-muted/40" data-testid="cost-sheet-upload">
              <span><span className="block text-sm font-medium">{reading ? 'Reading the cost sheet…' : 'Upload a processor cost sheet (PDF or Excel)'}</span>
                <span className="text-xs text-muted-foreground">AI fills in the lines below. Review and correct every amount, then save.</span></span>
              <input type="file" accept=".pdf,.xlsx,.xls,.csv" className="hidden" disabled={reading} onChange={(e) => { readSheet(e.target.files?.[0]); e.target.value = ''; }} data-testid="cost-sheet-input" />
              <span className="rounded-md border px-3 py-1.5 text-xs">{reading ? '…' : 'Choose file'}</span>
            </label>
          )}
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="TSYS MSC Standard" data-testid="template-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Processor</Label>
              <Select value={processorSlug} onValueChange={setProcessorSlug}>
                <SelectTrigger data-testid="template-processor-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORM_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Effective Date</Label>
              <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <Label>Cost Lines ({costLines.length})</Label>
            <div className="mt-2 space-y-1" data-testid="template-cost-lines">
              {groupByCategory(costLines.map((l, i) => ({ ...l, _i: i }))).map(([cat, lines]) => (
                <div key={cat} className="space-y-1">
                  <p className="pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{cat}</p>
                  {lines.map((l) => (
                    <div key={l._i} className="grid grid-cols-12 items-center gap-2" data-testid={`cost-line-${l._i}`}>
                      <div className="col-span-5 text-xs">{l.label}{l.feeds_payouts && <span className="ml-1 rounded bg-sky-100 px-1 text-[10px] text-sky-800">payouts</span>}{l.ai_extracted && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800">AI — check</span>}
                        <p className="text-[10px] text-muted-foreground">Schedule A: {l.billing_basis}</p></div>
                      <Select value={l.unit} onValueChange={(v) => setLine(l._i, 'unit', v)}>
                        <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="col-span-3 h-8 text-xs" type="number" step={0.0001} value={l.amount} disabled={l.unit === 'info'}
                        onChange={(e) => setLine(l._i, 'amount', e.target.value)} data-testid={`cost-line-amount-${l._i}`} />
                      <Button variant="ghost" size="icon" className="col-span-1 h-8" onClick={() => setCostLines(costLines.filter((_, j) => j !== l._i))}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !costLines.length} data-testid="template-save-btn">
            {saving ? 'Saving…' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
